import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress, Divider, Tab, Tabs, Typography } from '@mui/material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';
import type { FlowControlPerformMode, OutputPreviewTab } from '@/types/player/simulation-cue-sheet.types';
import { SimulationOutputPreview } from '@/components/preview/simulation-output-preview';
import { LiveKitOutputConnector } from '@/components/preview/livekit-output-connector';
import { PreviewAudioToggleButton } from '@/components/preview/preview-audio-toggle-button';
import type { OutputLayer } from '@/components/preview/output-layer-preview';
import { getOnAirLivekitRoomIdFromFlow, getEntityRefFromFlow } from '@/helpers/entity-ref.helper';
import {
  resolveCurrentPlayoutLayers,
  resolveSimulationPlayerType,
  isLiveKitPreviewLayer,
} from '@/helpers/resolve-output-preview-layers';
import { GET_LIVE_CONTEXT_STATE_QUERY_KEY } from '@/state/queries/player/use-get-live-context-state';
import { useGetFlowMediaAgentQuery } from '@/state/queries/playout/use-get-flow-media-agent';
import { resolveFlowEntityIdForPlayers } from '@/helpers/object.id.helper';
import { useSimulationPreviewLayersMutation } from '@/state/mutations/player/use-simulation-preview-layers';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/i18n/use-translations';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import {
  buildSimulationPreviewItemIds,
  ensureSimulationBroadcastRoomCamIds,
  SIMULATION_ROOM_CAM_PLACEHOLDER_ID,
} from '@/helpers/simulation-cue-engine.helper';
import { isRoomCamPreviewComponent, withBroadcastCameraPlaceholder } from '@/helpers/preview/room-cam-tile.helper';
import { enrichLivePreviewLayersWithSequencedPlayback } from '@/helpers/preview/enrich-live-preview-layers.helper';
import { getObjectId } from '@/helpers/object.id.helper';
import { useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { resolveLayerSequenceItemId } from '@/helpers/resolve-live-sequence-item-ids.helper';
import { LiveSlideControlProvider } from '@/components/preview/live-slide-control.context';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { useLiveContextCueState } from '@/hooks/use-live-context-cue-state';
import { FlowControlPreviewFrame } from './flow-control-preview-frame';
import { applyPresentationTransitionShield } from '@/helpers/preview/presentation-transition-shield.helper';
import { isOperatorCurrentPaintReady } from '@/helpers/preview/current-playout-paint-ready.helper';
import { isMediaAgentBackupSlidesActive } from '@/helpers/media-agent-recovery.helper';
import {
  resolveOperatorCurrentAgentHide,
  resolveOperatorCurrentAgentHideSpinner,
  resolveOperatorCurrentMediaEngineSwitchLabel,
} from '@/helpers/preview/resolve-operator-current-agent-shield.helper';
import { useLiveSlideChromeSuppressStore } from '@/state/stores/use-live-slide-chrome-suppress.store';
import { collectFlowMediaById } from '@/helpers/simulation-cue-playability.helper';
import { liveCueSheetToSimulationRows } from '@/helpers/live-cue-sheet-to-simulation-rows.helper';
import { isAgentDeckItemId } from '@/helpers/is-agent-deck-item.helper';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';

export interface FlowControlOutputPreviewProps {
  flow: FlowDto;
  performMode: FlowControlPerformMode;
  liveContextState?: LiveContextStateDto;
  planFlowId?: string;
  /** Rendered inside the 16:9 frame (e.g. Now label). */
  frameOverlay?: ReactNode;
  /** Optional frame border color (e.g. `error.main` for Now). */
  frameBorderColor?: string;
  /** Theme border width units (Now/Next stack uses 2). */
  frameBorderWidth?: number;
  /** When false, tabs are omitted (parent may render a shared tab bar). Default true. */
  showTabs?: boolean;
  /** Controlled tab when used inside Now/Next stack. */
  tab?: OutputPreviewTab;
  onTabChange?: (tab: OutputPreviewTab) => void;
  /** Now/Next stack: reports when layers + still-frame media are ready. */
  onReadyChange?: (ready: boolean) => void;
  /** Controlled On Air / Simulation preview listen (parent may render the toggle). */
  previewAudioEnabled?: boolean;
  onPreviewAudioEnabledChange?: (enabled: boolean) => void;
}

export function FlowControlOutputPreview({
  flow,
  performMode,
  liveContextState: liveContextStateFromGet,
  planFlowId: planFlowIdProp,
  frameOverlay,
  frameBorderColor,
  frameBorderWidth,
  showTabs = true,
  tab: tabProp,
  onTabChange,
  onReadyChange,
  previewAudioEnabled: previewAudioEnabledProp,
  onPreviewAudioEnabledChange,
}: FlowControlOutputPreviewProps) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const [uncontrolledTab, setUncontrolledTab] = useState<OutputPreviewTab>('stageRoom');
  const [internalPreviewAudioEnabled, setInternalPreviewAudioEnabled] = useState(false);
  const previewAudioEnabled = previewAudioEnabledProp ?? internalPreviewAudioEnabled;
  const setPreviewAudioEnabled = (enabled: boolean) => {
    onPreviewAudioEnabledChange?.(enabled);
    if (previewAudioEnabledProp === undefined) {
      setInternalPreviewAudioEnabled(enabled);
    }
  };
  const tab = tabProp ?? uncontrolledTab;
  const setTab = (next: OutputPreviewTab) => {
    onTabChange?.(next);
    if (tabProp == null) {
      setUncontrolledTab(next);
    }
  };
  const planFlowId = planFlowIdProp ?? (flow.origin ? getObjectId(flow.origin) : flow.id);
  const livekitRoomId = getOnAirLivekitRoomIdFromFlow(planFlowId ?? '', flow.liveContext);
  const entityRef = useMemo(
    () => getEntityRefFromFlow(planFlowId ?? '', flow.liveContext),
    [planFlowId, flow.liveContext],
  );
  const liveContextState = useLiveContextStateWithLivePlayState(entityRef, liveContextStateFromGet);
  const backupSlidesActive = performMode === 'play' && isMediaAgentBackupSlidesActive(liveContextState?.playState);

  const simulationOnAirShowMode = useFlowControlSimulationCueStore((s) => s.onAirShowMode);
  const simulationSelectedItemIds = useFlowControlSimulationCueStore((s) => s.selectedItemIds);
  const simulationPointer = useFlowControlSimulationCueStore((s) => s.pointer);
  const simulationOverridePointer = useFlowControlSimulationCueStore((s) => s.overridePointer);
  const simulationOnAirPointer = simulationOverridePointer ?? simulationPointer;
  const simulationRows = useFlowControlSimulationCueStore((s) => s.rows);

  const contextSlotId =
    simulationRows[simulationOnAirPointer]?.slotId ??
    (simulationRows[simulationOnAirPointer]?.segmentAnchor?.startsWith('slot:')
      ? simulationRows[simulationOnAirPointer]!.segmentAnchor.slice('slot:'.length)
      : undefined);
  const contextScope = simulationRows[simulationOnAirPointer]?.scope;

  const simulationPreviewItemIds = useMemo(
    () =>
      buildSimulationPreviewItemIds({
        rows: simulationRows,
        pointer: simulationOnAirPointer,
        selectedItemIds: simulationSelectedItemIds,
        showMode: simulationOnAirShowMode,
        // Broadcast only — stagePlayer ignores roomCam layers; Stage tab must not force cam into template.
        ensureRoomCamPlaceholder: tab === 'live',
      }),
    [simulationRows, simulationOnAirPointer, simulationSelectedItemIds, simulationOnAirShowMode, tab],
  );

  const previewLayersMutation = useSimulationPreviewLayersMutation();
  const [apiPreviewLayers, setApiPreviewLayers] = useState<OutputLayer[]>([]);
  const [layersRequestReady, setLayersRequestReady] = useState(true);
  const [mediaReady, setMediaReady] = useState(false);

  // Simulation builds its picture over HTTP. Play paints the type `_preview:*` snapshot instead.
  const previewLayersRequest = useMemo(() => {
    if (performMode !== 'simulation' || !simulationPreviewItemIds.length) {
      return null;
    }
    return {
      selectedItemIds: simulationPreviewItemIds,
      showMode: simulationOnAirShowMode,
      contextSlotId,
      contextScope,
    };
  }, [performMode, simulationPreviewItemIds, simulationOnAirShowMode, contextSlotId, contextScope]);

  const previewRequestKey = useMemo(() => {
    if (!planFlowId || !previewLayersRequest?.selectedItemIds.length) {
      return '';
    }
    return JSON.stringify({
      planFlowId,
      tab,
      ...previewLayersRequest,
    });
  }, [planFlowId, tab, previewLayersRequest]);

  const debouncedPreviewRequestKey = useDebounce(previewRequestKey, 300);
  /**
   * Layers cache keyed by full request (playerType + selectedItemIds including roomCam).
   * Do not key only by playerType — Stage prefetch of Broadcast without roomCam must not
   * satisfy a later Broadcast request that needs the mixed-view cam placeholder.
   */
  const mediaScopeRef = useRef('');
  const layersCacheRef = useRef(new Map<string, OutputLayer[]>());
  const mutatePreviewLayersRef = useRef(previewLayersMutation.mutateAsync);

  useEffect(() => {
    mutatePreviewLayersRef.current = previewLayersMutation.mutateAsync;
  }, [previewLayersMutation.mutateAsync]);

  useEffect(() => {
    if (!planFlowId || !debouncedPreviewRequestKey) {
      setApiPreviewLayers([]);
      setLayersRequestReady(true);
      mediaScopeRef.current = '';
      layersCacheRef.current.clear();
      return;
    }
    const parsed = JSON.parse(debouncedPreviewRequestKey) as {
      planFlowId: string;
      tab: OutputPreviewTab;
      showMode: 'mediaOnly' | 'both' | 'speakerOnly';
      selectedItemIds: string[];
      contextSlotId?: string;
      contextScope?: string;
    };

    const contentItemIds = parsed.selectedItemIds.filter(
      (id) => !id.startsWith('remoteSpeaker:') && !id.startsWith('roomCam:'),
    );
    // Soft ready-gate: cue/media/showMode changes (not Stage↔Broadcast roomCam-only diffs).
    const mediaScope = JSON.stringify({
      planFlowId: parsed.planFlowId,
      selectedItemIds: contentItemIds,
      showMode: parsed.showMode,
      contextSlotId: parsed.contextSlotId,
      contextScope: parsed.contextScope,
    });
    const playerType = resolveSimulationPlayerType(parsed.tab);
    // Broadcast needs a roomCam id for both/speakerOnly; Stage must not send the synthetic cam.
    const requestSelectedItemIds =
      playerType === 'onlinePlayer'
        ? ensureSimulationBroadcastRoomCamIds(parsed.selectedItemIds, parsed.showMode)
        : parsed.selectedItemIds.filter((id) => id !== SIMULATION_ROOM_CAM_PLACEHOLDER_ID);

    const layerCacheKey = JSON.stringify({
      playerType,
      planFlowId: parsed.planFlowId,
      showMode: parsed.showMode,
      selectedItemIds: requestSelectedItemIds,
      contextSlotId: parsed.contextSlotId,
      contextScope: parsed.contextScope,
    });

    const mediaScopeChanged = mediaScope !== mediaScopeRef.current;
    mediaScopeRef.current = mediaScope;
    if (mediaScopeChanged) {
      layersCacheRef.current.clear();
      setLayersRequestReady(false);
    }

    const cached = layersCacheRef.current.get(layerCacheKey);
    if (cached) {
      setApiPreviewLayers(cached);
      setLayersRequestReady(true);
      return;
    }

    let cancelled = false;
    void mutatePreviewLayersRef
      .current({
        planFlowId: parsed.planFlowId,
        selectedItemIds: requestSelectedItemIds,
        showMode: parsed.showMode,
        playerType,
        contextSlotId: parsed.contextSlotId,
        contextScope: parsed.contextScope,
      })
      .then((result) => {
        layersCacheRef.current.set(layerCacheKey, result.layers);
        if (!cancelled) {
          setApiPreviewLayers(result.layers);
          setLayersRequestReady(true);
        }
        // Prefetch the other tab with the correct roomCam shape for Broadcast.
        const otherTab: OutputPreviewTab = parsed.tab === 'stageRoom' ? 'live' : 'stageRoom';
        const otherPlayerType = resolveSimulationPlayerType(otherTab);
        const otherSelectedItemIds =
          otherPlayerType === 'onlinePlayer'
            ? ensureSimulationBroadcastRoomCamIds(contentItemIds, parsed.showMode)
            : contentItemIds;
        const otherCacheKey = JSON.stringify({
          playerType: otherPlayerType,
          planFlowId: parsed.planFlowId,
          showMode: parsed.showMode,
          selectedItemIds: otherSelectedItemIds,
          contextSlotId: parsed.contextSlotId,
          contextScope: parsed.contextScope,
        });
        if (layersCacheRef.current.has(otherCacheKey)) {
          return;
        }
        if (mediaScopeRef.current !== mediaScope) {
          return;
        }
        void mutatePreviewLayersRef
          .current({
            planFlowId: parsed.planFlowId,
            selectedItemIds: otherSelectedItemIds,
            showMode: parsed.showMode,
            playerType: otherPlayerType,
            contextSlotId: parsed.contextSlotId,
            contextScope: parsed.contextScope,
          })
          .then((other) => {
            if (mediaScopeRef.current !== mediaScope) {
              return;
            }
            layersCacheRef.current.set(otherCacheKey, other.layers);
          })
          .catch(() => {
            // Prefetch is best-effort; next tab switch will fetch on demand.
          });
      })
      .catch(() => {
        if (!cancelled) {
          setApiPreviewLayers([]);
          setLayersRequestReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedPreviewRequestKey, planFlowId]);

  const rawLayers = useMemo(
    () => (performMode === 'play' ? resolveCurrentPlayoutLayers(liveContextState, tab) : apiPreviewLayers),
    [apiPreviewLayers, performMode, tab, liveContextState],
  );

  useEffect(() => {
    if (performMode !== 'play' || !entityRef) {
      return;
    }
    void queryClient.refetchQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
  }, [entityRef, performMode, queryClient]);

  const chromeSuppressEntry = useLiveSlideChromeSuppressStore((s) =>
    entityRef ? s.byEntityRef[entityRef] : undefined,
  );
  const chromeSuppressed = Boolean(chromeSuppressEntry);
  const bePresentationTransitionInFlight =
    performMode === 'play' && liveContextState?.playState?.presentationTransitionInFlight === true;
  const mergedCueState = useLiveContextCueState(
    performMode === 'play' ? entityRef : undefined,
    undefined,
    liveContextState?.cueState,
  );
  const cueState = performMode === 'play' ? (mergedCueState ?? liveContextState?.cueState) : liveContextState?.cueState;
  const cueCurrentItemId = cueState?.currentStep?.itemId;
  const liveSheetRoomKey = entityRef ? entityRoomKeyFromEntityRef(entityRef) : null;
  const wsCueSheet = usePerformControlWsStore((s) =>
    liveSheetRoomKey ? s.cueSheetByRoom[liveSheetRoomKey] : undefined,
  );
  const liveCueSheet = wsCueSheet ?? liveContextState?.cueSheet;
  const liveSheetRows = useMemo(
    () => (performMode === 'play' && liveCueSheet ? liveCueSheetToSimulationRows(liveCueSheet) : []),
    [performMode, liveCueSheet],
  );
  const agentDeckRows = liveSheetRows.length > 0 ? liveSheetRows : simulationRows;
  const isPresentationFileOnFlow = useCallback(
    (itemId: string): boolean => {
      if (!itemId.startsWith('media:')) {
        return false;
      }
      const media = collectFlowMediaById(flow).get(itemId.slice('media:'.length));
      const asset = media?.assets?.find((entry) => entry.isApproved) ?? media?.assets?.[0];
      return asset?.fileType === 'presentation';
    },
    [flow],
  );
  const { data: mediaAgentSetting } = useGetFlowMediaAgentQuery(resolveFlowEntityIdForPlayers(flow));
  const presentationComputerOff =
    mediaAgentSetting != null && (!mediaAgentSetting.entitled || !mediaAgentSetting.enabled);
  const itemIsAgentDeck = useCallback(
    (itemId: string | undefined): boolean => {
      if (presentationComputerOff) {
        return false;
      }
      return isAgentDeckItemId(itemId, {
        cueSheet: liveCueSheet,
        rows: agentDeckRows,
        isPresentationFile: isPresentationFileOnFlow,
      });
    },
    [agentDeckRows, isPresentationFileOnFlow, liveCueSheet, presentationComputerOff],
  );
  const takingAgentDeck = itemIsAgentDeck(chromeSuppressEntry?.toItemId);
  const mediaEngineDeckSwitch = Boolean(chromeSuppressEntry?.mediaEngineDeckSwitch);
  const waitForPresentationShield = mediaEngineDeckSwitch || takingAgentDeck;
  const currentIsAgentDeck = itemIsAgentDeck(cueCurrentItemId);
  const takeTargetItemId = chromeSuppressed ? (chromeSuppressEntry?.toItemId ?? cueCurrentItemId) : cueCurrentItemId;
  const takeTargetIsAgentDeck = itemIsAgentDeck(takeTargetItemId);
  const currentPaintReady =
    backupSlidesActive ||
    (performMode === 'play' &&
      isOperatorCurrentPaintReady({
        currentItemId: takeTargetItemId,
        isAgentDeck: takeTargetIsAgentDeck,
        layers: rawLayers,
        activeAgentDeckMediaId: liveContextState?.playState?.activeAgentDeckMediaId,
      }));
  // Track/clear chrome suppress from the always-mounted Current preview (overlay can miss inFlight).
  useEffect(() => {
    if (performMode !== 'play' || !entityRef) {
      return;
    }
    useLiveSlideChromeSuppressStore.getState().syncClearAfterAdvance(entityRef, {
      currentItemId: cueCurrentItemId,
      presentationTransitionInFlight: bePresentationTransitionInFlight,
      waitForPresentationShield,
      onAirPaintReady: currentPaintReady,
    });
  }, [
    performMode,
    entityRef,
    cueCurrentItemId,
    bePresentationTransitionInFlight,
    waitForPresentationShield,
    currentPaintReady,
  ]);
  const hideAgentLiveKitLikePlayers = resolveOperatorCurrentAgentHide({
    performMode,
    presentationTransitionInFlight: bePresentationTransitionInFlight,
    currentPaintReady,
  });
  const showCurrentAgentHideSpinner = resolveOperatorCurrentAgentHideSpinner({
    hideAgent: hideAgentLiveKitLikePlayers,
    currentIsAgentDeck: currentIsAgentDeck || takingAgentDeck || mediaEngineDeckSwitch,
    currentPaintReady,
  });
  const showMediaEngineSwitchLabel = resolveOperatorCurrentMediaEngineSwitchLabel({
    spinner: showCurrentAgentHideSpinner,
    mediaEngineDeckSwitch,
    // Play once insert can clear click chrome while cue current is already the PPT and inFlight
    // is still waiting on the VM deck switch — keep the same copy as jump/advance onto a deck.
    takingAgentDeck: takingAgentDeck || takeTargetIsAgentDeck,
  });

  const layers = useMemo(() => {
    if (performMode !== 'play') {
      return rawLayers;
    }
    const enriched = enrichLivePreviewLayersWithSequencedPlayback(rawLayers, {
      liveContextState,
      tab,
      simulationRows: simulationRows,
    });
    const shielded = applyPresentationTransitionShield(enriched, hideAgentLiveKitLikePlayers);
    // A hidden camera row is the players' choice (e.g. a composite over speaker-only); only fill a missing slot.
    if (shielded.some((layer) => isRoomCamPreviewComponent(layer.componentId))) {
      return shielded;
    }
    return withBroadcastCameraPlaceholder(shielded, liveContextState?.playState?.showMode, tab);
  }, [performMode, rawLayers, liveContextState, tab, simulationRows, hideAgentLiveKitLikePlayers]);

  const presentationLoadingLabel = t('flows.detail.control.play.presentationLoading', {
    defaultValue: 'Switching deck on media engine',
  });
  const currentLoadingLabel = t('flows.detail.control.nowNext.loading', { defaultValue: 'Loading previews' });
  const currentBusyLabel = showMediaEngineSwitchLabel ? presentationLoadingLabel : currentLoadingLabel;

  const composedFrameOverlay = useMemo(
    () => (
      <>
        {frameOverlay}
        {showCurrentAgentHideSpinner ? (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: 'rgba(0,0,0,0.65)',
              px: 2,
            }}
          >
            <CircularProgress size={42} sx={{ color: '#fff' }} aria-label={currentBusyLabel} />
            {showMediaEngineSwitchLabel ? (
              <Typography
                variant="body2"
                sx={{
                  color: '#fff',
                  textAlign: 'center',
                  letterSpacing: '0.02em',
                  opacity: 0.92,
                }}
              >
                {presentationLoadingLabel}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </>
    ),
    [frameOverlay, currentBusyLabel, presentationLoadingLabel, showCurrentAgentHideSpinner, showMediaEngineSwitchLabel],
  );

  useEffect(() => {
    if (performMode !== 'play') {
      return;
    }
    // Do not seed from layers mid-transition — open@1 / stale stamps flash the Current badge.
    if (hideAgentLiveKitLikePlayers) {
      return;
    }
    const liveStore = useLiveSequenceIndexStore.getState();
    for (const layer of layers) {
      if (layer.componentId !== 'imageSequence' && layer.componentId !== 'presentationSlides') {
        continue;
      }
      const itemId = resolveLayerSequenceItemId(layer);
      if (!itemId) {
        continue;
      }
      const fromLayer = layer.data?.currentIndex ? Number.parseInt(layer.data.currentIndex, 10) : undefined;
      // Layer stamps often lag at open@1 — only seed when the live store has no value yet.
      const existing = liveStore.indexByItemId[itemId];
      // A layer stamp often lags at the first frame. It may seed an empty store, or catch up
      // when it is ahead. It must not pull an auto-loop that has already moved back to frame 1.
      const liveTimingHoldsIndex = liveStore.isActiveStepTiming(itemId);
      if (
        fromLayer != null &&
        fromLayer > 0 &&
        !liveStore.isRecentlyOptimistic(itemId) &&
        (existing == null || existing < 1 || (!liveTimingHoldsIndex && fromLayer > existing))
      ) {
        liveStore.setIndex(itemId, fromLayer);
      }
      const frameCount = layer.data?.frameCount
        ? Number.parseInt(layer.data.frameCount, 10)
        : layer.data?.slideCount
          ? Number.parseInt(layer.data.slideCount, 10)
          : undefined;
      if (frameCount != null && frameCount > 0) {
        liveStore.reportTotalPages(itemId, frameCount);
      }
    }
  }, [performMode, layers, hideAgentLiveKitLikePlayers]);

  const liveSlideControlContext = useMemo(() => {
    if (performMode !== 'play') {
      return null;
    }
    const liveContext = flow.liveContext;
    if (liveContext?.isRunning && liveContext.entityType && liveContext.entityId) {
      return { entityType: liveContext.entityType, entityId: liveContext.entityId };
    }
    if (!planFlowId) {
      return null;
    }
    return { entityType: 'Flow' as const, entityId: planFlowId };
  }, [performMode, flow.liveContext, planFlowId]);

  const onReadyChangeRef = useRef(onReadyChange);
  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  // Play is ready once the players' layers are here; LiveKit tiles also need the room connected.
  const needsLiveKitRoom =
    Boolean(livekitRoomId) && layers.some((layer) => layer.visible !== false && isLiveKitPreviewLayer(layer));
  const currentReady =
    performMode === 'play' ? layers.length > 0 && (!needsLiveKitRoom || mediaReady) : layersRequestReady;
  useEffect(() => {
    onReadyChangeRef.current?.(currentReady);
  }, [currentReady]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, gap: showTabs ? 1 : 0 }}>
      {showTabs && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          <Tabs
            value={tab}
            onChange={(_, value: OutputPreviewTab) => setTab(value)}
            sx={{ 'minHeight': 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
          >
            <Tab
              value="stageRoom"
              label={t('flows.detail.control.outputPreview.tabs.stageRoom', { defaultValue: 'Stage / Room' })}
            />
            <Tab
              value="live"
              label={t('flows.detail.control.outputPreview.tabs.live', { defaultValue: 'Broadcast' })}
            />
          </Tabs>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
          <PreviewAudioToggleButton
            variant="inline"
            audioEnabled={previewAudioEnabled}
            onToggle={() => setPreviewAudioEnabled(!previewAudioEnabled)}
          />
        </Box>
      )}

      <FlowControlPreviewFrame
        overlay={composedFrameOverlay}
        borderColor={frameBorderColor}
        borderWidth={frameBorderWidth}
      >
        <LiveSlideControlProvider value={liveSlideControlContext}>
          {layers.length === 0 ? (
            <Box
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                textAlign: 'center',
                bgcolor: '#000',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {performMode === 'simulation'
                  ? t('flows.detail.control.simulation.noOutput', {
                      defaultValue: 'Select media in the cue list or use transport to preview output.',
                    })
                  : t('flows.detail.control.play.noOutput', {
                      defaultValue: 'Start the flow and transition to see live output.',
                    })}
              </Typography>
            </Box>
          ) : performMode === 'simulation' ? (
            <SimulationOutputPreview
              layers={layers}
              height="100%"
              stillFrame={false}
              previewAudioEnabled={previewAudioEnabled}
              onPreviewAudioEnabledChange={setPreviewAudioEnabled}
              showInFrameAudioToggle={false}
            />
          ) : livekitRoomId ? (
            <LiveKitOutputConnector
              livekitRoomId={livekitRoomId}
              layers={layers}
              height="100%"
              slideNavigationScope="live"
              sequencePlaybackControls={false}
              showAgentDeckSlideOverlay
              liveContextState={liveContextState}
              entityRef={entityRef}
              stillFrame={false}
              syncFileVideo
              onReadyChange={setMediaReady}
              previewAudioEnabled={previewAudioEnabled}
              onPreviewAudioEnabledChange={setPreviewAudioEnabled}
              showInFrameAudioToggle={false}
            />
          ) : (
            <SimulationOutputPreview
              layers={layers}
              height="100%"
              slideNavigationScope="live"
              preferAbsoluteSlideGoto
              sequencePlaybackControls={false}
              liveOnAirItemId={cueCurrentItemId}
              stillFrame={false}
              syncFileVideo
              previewAudioEnabled={previewAudioEnabled}
              onPreviewAudioEnabledChange={setPreviewAudioEnabled}
              showInFrameAudioToggle={false}
            />
          )}
        </LiveSlideControlProvider>
      </FlowControlPreviewFrame>
    </Box>
  );
}
